import { Navigate } from 'react-router-dom';
import { CTASection } from '../../components/CTASection';
import { PageHero } from '../../components/PageHero';
import { PricingCard } from '../../components/PricingCard';
import { Section, SectionHeading, Reveal } from '../../components/Section';
import { getServiceBySlug, type ServiceSlug } from '../../data/services';
import { Icon } from '../../lib/icons';
import { SEO } from '../../lib/seo';

export function ServiceDetailPage({ slug }: { slug: ServiceSlug }) {
  const service = getServiceBySlug(slug);

  if (!service) return <Navigate to="/services" replace />;

  return (
    <>
      <SEO
        title={service.title}
        description={`${service.summary} MaytoMation provides ${service.shortTitle.toLowerCase()} services for Massachusetts businesses with documentation-first delivery and long-term support.`}
        keywords={[service.eyebrow, `${service.shortTitle} Massachusetts`, 'MaytoMation', 'business technology support']}
      />
      <PageHero
        eyebrow={service.eyebrow}
        title={service.title}
        body={service.hero}
        primary={{ label: 'Request Consultation', href: '/booking' }}
        secondary={{ label: 'View Pricing', href: '/pricing' }}
        highlights={service.outcomes}
      />

      <Section>
        <div className="grid gap-10 lg:grid-cols-[.9fr_1.1fr] lg:items-start">
          <Reveal>
            <SectionHeading
              eyebrow="Problems Solved"
              title={`When ${service.shortTitle.toLowerCase()} work becomes operationally important, it needs structure.`}
              body={service.summary}
            />
          </Reveal>
          <div className="grid gap-4">
            {service.problems.map((problem) => (
              <Reveal key={problem}>
                <div className="flex gap-4 rounded-lg border border-brand-border bg-white p-5 shadow-card">
                  <Icon name="check" className="mt-1 h-5 w-5 shrink-0 text-brand-teal" />
                  <p className="text-base font-semibold leading-7 text-brand-dark">{problem}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section className="bg-white">
        <SectionHeading
          eyebrow="Project Examples"
          title="Realistic ways this service shows up inside a business."
          body="Every engagement is scoped to the current operation, risk profile, and systems already in place."
        />
        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {service.examples.map((example) => (
            <Reveal key={example.title}>
              <article className="h-full rounded-lg border border-brand-border bg-brand-background p-6 shadow-card">
                <h3 className="text-xl font-extrabold text-brand-dark">{example.title}</h3>
                <p className="mt-4 text-sm leading-7 text-brand-muted">{example.description}</p>
                <div className="mt-6 rounded-md border border-brand-border bg-white p-4">
                  <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-teal">Outcome</p>
                  <p className="mt-2 text-sm font-bold leading-6 text-brand-navy">{example.outcome}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Planning Estimates"
          title="Pricing ranges for early planning."
          body="Final scope depends on integrations, risk, user roles, support expectations, and the condition of existing systems."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {service.pricing.map((item) => (
            <PricingCard key={item.label} title={item.label} price={item.price} description={item.note} />
          ))}
        </div>
      </Section>

      <Section className="bg-white">
        <SectionHeading eyebrow="FAQs" title={`Questions about ${service.shortTitle.toLowerCase()} work.`} />
        <div className="mt-10 grid gap-4">
          {service.faqs.map((faq) => (
            <Reveal key={faq.question}>
              <details className="group rounded-lg border border-brand-border bg-brand-background p-6 shadow-sm">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-extrabold text-brand-dark">
                  {faq.question}
                  <Icon name="chevronDown" className="h-5 w-5 shrink-0 text-brand-navy transition group-open:rotate-180" />
                </summary>
                <p className="mt-4 text-sm leading-7 text-brand-muted">{faq.answer}</p>
              </details>
            </Reveal>
          ))}
        </div>
      </Section>

      <CTASection title={`Ready to discuss ${service.shortTitle.toLowerCase()}?`} button="Schedule a Consultation" />
    </>
  );
}
